/**
 * @file Hook d'accès à la configuration d'affichage des niveaux de fidélité.
 * @module hooks.useStatusConfig
 *
 * Source unique côté frontend pour l'apparence des niveaux de fidélité,
 * personnalisée par établissement. Récupère la CustomerConfig de l'établissement
 * actif et en dérive la map d'affichage (libellé/icône/couleur/tooltip) et les
 * options de filtre. Retombe sur les valeurs par défaut tant que la config n'est
 * pas chargée ou hors contexte établissement.
 *
 * La clé du niveau (customer.statusLevel) reste calculée côté backend.
 */

import {useMemo} from 'react';
import {useActiveCompany} from 'packages/common/hooks/session';
import {useGetCustomerConfig} from 'packages/common/hooks/api/app/customers/Customer';
import {buildStatusVisuals} from 'packages/common/components/customer/constants';

/**
 * Retourne les helpers d'affichage des niveaux de fidélité de l'établissement.
 *
 * @return {{getConfig: Function, levelMap: Object, filterOptions: Array, isLoading: boolean}}
 *   - getConfig(level) : config visuelle d'un niveau (repli sur le niveau de base)
 *   - levelMap : map clé -> config visuelle
 *   - filterOptions : options du filtre de fidélité (avec 'Tous')
 *   - isLoading : true tant que la config n'est pas résolue
 */
export function useStatusConfig() {
  const {companyId} = useActiveCompany();
  const {config, isLoading} = useGetCustomerConfig(companyId);

  const visuals = useMemo(() => buildStatusVisuals(config?.statusLevels?.tiers), [config]);

  return {...visuals, isLoading};
}
